"use client";

import { motion } from "framer-motion";
import { Globe2, Ship, Plane, Package, Flag, Star, ArrowRight, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const regions = [
  {
    name: "Nigeria",
    flag: "🇳🇬",
    type: "Grande Commande Oumma",
    volume: "50+ tonnes / mois",
    desc: "Premier partenaire commercial de la zone CEDEAO. Approvisionnement régulier des marchés de Lagos, Kano, Abuja et Port Harcourt en patate douce, manioc et igname.",
    products: ["Patate douce", "Manioc frais", "Igname", "Farine de manioc"],
    delay: "3–5 jours",
  },
  {
    name: "Europe",
    flag: "🇪🇺",
    type: "Export Premium",
    volume: "20+ tonnes / mois",
    desc: "Marchés diaspora africaine et bio : France, Belgique, Italie, Royaume-Uni, Allemagne. Produits transformés certifiés bio et équitables, attiéké, farines, chips.",
    products: ["Attiéké", "Farine de manioc", "Chips de patate", "Igname premium"],
    delay: "10–15 jours maritime · 48h aérien",
  },
  {
    name: "Oumma — Monde Musulman",
    flag: "🕌",
    type: "Commerce fraternel",
    volume: "Sur commande",
    desc: "Arabie Saoudite, Émirats, Qatar, Turquie, Sénégal, Mali, Guinée... Export halal certifié, en particulier pendant le Ramadan et les fêtes. Logistique spiritually aligned.",
    products: ["Dattes fourrées à la patate", "Plats préparés halal", "Farines", "Igname festif"],
    delay: "15–25 jours",
  },
  {
    name: "Reste du Monde",
    flag: "🌍",
    type: "À la demande",
    volume: "Sur devis",
    desc: "Amériques, Asie, Océanie : nous étudions toute demande d'exportation sérieuse. Documentation phytosanitaire, douanes, certifications spécifiques prises en charge.",
    products: ["Sur mesure", "Conditionnement export", "Certifications"],
    delay: "Selon destination",
  },
];

const exportSteps = [
  { icon: Package, title: "Commande", text: "Réception, validation, devis CIF/FOB." },
  { icon: Flag, title: "Certification", text: "Phytosanitaire, halal, bio selon destination." },
  { icon: Ship, title: "Conditionnement", text: "Emballage export, palettisation, conteneur." },
  { icon: Globe2, title: "Expédition", text: "Maritime ou aérien, suivi de cargaison." },
];

export function Export() {
  return (
    <section id="export" className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background image with overlay */}
      <div className="absolute inset-0 -z-10">
        <img
          src="/images/export-port.png"
          alt="Port d'exportation international"
          className="w-full h-full object-cover"
        />
        {/* Stronger overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-earth-deep via-earth-deep/97 to-earth-deep" />
        <div className="absolute inset-0 bg-earth-deep/40" />
      </div>

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cream/95 text-earth-deep text-xs sm:text-sm font-bold mb-5 shadow-gold"
          >
            <Globe2 className="w-4 h-4 text-gold-deep" />
            <span className="tracking-wide">Exportation Internationale · Oumma · Monde</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-cream drop-shadow-[0_4px_24px_rgba(0,0,0,0.7)]"
          >
            Du <span className="text-gradient-gold">champ africain</span> à la{" "}
            <span className="text-gradient-gold">table du monde</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-base sm:text-lg text-cream/95 leading-relaxed drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]"
          >
            PATATE OR exporte vers les grandes capitales de l&apos;Oumma, du Nigeria
            voisin jusqu&apos;aux marchés européens et au-delà. Notre logistique
            internationale, nos certifications et notre savoir-faire douanier garantissent
            une livraison premium, partout dans le monde.
          </motion.p>
        </div>

        {/* World map */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-14 relative max-w-3xl mx-auto rounded-3xl overflow-hidden shadow-gold-lg ring-1 ring-gold/30"
        >
          <img
            src="/images/export-map.png"
            alt="Carte mondiale des routes d'exportation PATATE OR"
            className="w-full h-72 sm:h-96 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-earth-deep/40 to-transparent" />
        </motion.div>

        {/* Regions grid */}
        <div className="mt-16 grid md:grid-cols-2 gap-6">
          {regions.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="group relative p-6 sm:p-8 rounded-3xl bg-card border border-gold/40 hover:border-gold hover:shadow-gold-lg transition-all duration-500 overflow-hidden"
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-center gap-4">
                  <span className="text-5xl" aria-hidden>{r.flag}</span>
                  <div>
                    <h3 className="font-display text-2xl font-bold text-foreground drop-shadow-sm">
                      {r.name}
                    </h3>
                    <Badge className="mt-1 bg-gradient-gold text-white border-0">
                      {r.type}
                    </Badge>
                  </div>
                </div>
              </div>

              <p className="text-sm text-foreground/80 leading-relaxed min-h-[80px]">
                {r.desc}
              </p>

              {/* Volume + delay */}
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-secondary border border-gold/30">
                  <div className="text-[10px] uppercase tracking-wider text-gold-deep font-bold">
                    Volume
                  </div>
                  <div className="font-display text-base font-bold text-foreground mt-1">
                    {r.volume}
                  </div>
                </div>
                <div className="p-3 rounded-xl bg-secondary border border-gold/30">
                  <div className="text-[10px] uppercase tracking-wider text-gold-deep font-bold">
                    Délai
                  </div>
                  <div className="font-display text-base font-bold text-foreground mt-1">
                    {r.delay}
                  </div>
                </div>
              </div>

              {/* Products */}
              <div className="mt-4 flex flex-wrap gap-2">
                {r.products.map((p, j) => (
                  <span
                    key={j}
                    className="inline-flex items-center px-2.5 py-1 rounded-full bg-secondary text-foreground/85 text-[11px] font-medium border border-border"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Export process */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-14 p-6 sm:p-10 rounded-3xl bg-card border border-gold/40 shadow-gold"
        >
          <div className="text-center mb-8">
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
              Notre processus d&apos;export
            </h3>
            <p className="mt-2 text-muted-foreground text-sm">
              Quatre étapes maîtrisées, du devis à la livraison internationale.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {exportSteps.map((s, i) => (
              <div key={i} className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="grid place-items-center w-10 h-10 rounded-full bg-gradient-gold text-white font-bold text-sm shadow-gold">
                    {i + 1}
                  </div>
                  <s.icon className="w-6 h-6 text-gold-deep" />
                </div>
                <h4 className="font-display text-lg font-bold text-foreground">
                  {s.title}
                </h4>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                  {s.text}
                </p>
                {i < exportSteps.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute -right-3 top-3 w-5 h-5 text-gold/40" />
                )}
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-14 text-center"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-6 rounded-2xl bg-gradient-gold shadow-gold-lg">
            <Star className="w-8 h-8 text-white fill-white" />
            <div className="text-left">
              <div className="font-display text-xl font-bold text-white">
                Importateur international ? Devenez partenaire.
              </div>
              <div className="text-white/90 text-sm mt-1">
                Demandez notre dossier export complet et nos tarifs CIF/FOB.
              </div>
            </div>
            <button
              onClick={() =>
                document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })
              }
              className="px-6 py-3 rounded-full bg-white text-earth-deep font-semibold hover:bg-cream transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <MapPin className="w-4 h-4" />
              Demander le dossier
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
